import { TokenResponse } from '../classes/token-response';
import { Router } from '@angular/router';
import { CredentialsService } from './credentials.service';
import { ObjectResponse } from '../classes/object-response';
import { ErrorResponse } from '../classes/error-response';
import { LoginSource } from '../classes/enums';
import { HttpClient } from '@angular/common/http';

import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment-wrapper';
import { ErrorKey, ErrorService } from './error.service';
import { Observable, of } from 'rxjs';
import { APP_CONFIG } from '../app-config.constants';
import { IAppConfig } from '../app-config.interface';
import { SCREEN_SIZE } from '../classes/enums';
import { CommunicationService } from './communication.service';
import { HasSubscriptions } from '../components/has-subscriptions';
import { flatMap, map, catchError } from 'rxjs/operators';

export interface ApiResponse<TActual> {

  error: ErrorResponse;
  response: TActual;
  requestId: string;
}

enum Method {
  Get,
  Post,
  Put,
  Delete
}

const MAX_UNAUTHORIZED_FAILURES = 1;

@Injectable()
export class ApiService extends HasSubscriptions {
  private screenSize: SCREEN_SIZE;

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig
    , private _http: HttpClient
    , private _credentials: CredentialsService
    , private _router: Router
    , private _errorService: ErrorService
    , private communicationService: CommunicationService
  ) {
    super();
    this.addSub(this.communicationService.screenSize.subscribe((screenSize) => this.screenSize = screenSize))
  }

  public forceRedirectToLogin() {
    if (this.screenSize < SCREEN_SIZE.DESKTOP_UP) {
      this._router.navigate(['/login']);
    } else {
      this._router.navigate(['/', { outlets: { popup: [ 'register-login', LoginSource.Generic ] } }])
    }
  }

  public forceRedirectToUnAuthPage() {
    this._router.navigate(['/not-authorized'], { skipLocationChange: true });
  }

  public setNetworkStatus(good: boolean) {
    if (!good) {
      this._errorService.displayError(ErrorKey.networkError);
    } else {
      this._errorService.clearError(ErrorKey.networkError);
    }
  }

  // Get api root
  public getApiRoot(): string {
    return environment.cdnServerUrl;
  }

  public getAccessToken() {
    return this._credentials.getAccessToken();
  }

  public restoreAccessToken(sessionCacheKey: string): Observable<boolean> {
    const apiRequest = new ApiRequest<TokenResponse>(this._http, this)
      .get(this.getApiRoot() + this.appConfig.urls.user.restoreSession + '?key=' + sessionCacheKey)
      .doesNotRequireTokenAuthentication()
      .disableAutomaticNotAuthorizedPage()
      .disableAutomaticRedirectToLogin()
      .disableAutomaticRefreshToken();

    return this.performRefreshAccessToken(apiRequest);

  }

  public getRefreshAccessToken(): Observable<boolean> {
    const apiRequest = new ApiRequest<TokenResponse>(this._http, this)
      .post(this.getApiRoot() + this.appConfig.urls.user.renewToken, {})
      .doesNotRequireTokenAuthentication()
      .disableAutomaticNotAuthorizedPage()
      .disableAutomaticRedirectToLogin()
      .disableAutomaticRefreshToken();

    return this.performRefreshAccessToken(apiRequest);
  }

  private performRefreshAccessToken(apiRequest: ApiRequest<TokenResponse>) {
    return apiRequest.build()
      .pipe(map(response => {
        if (response.error) {
          return false;
        }
        if (response && response.response) {
          const token = response.response;
          this._credentials.setTokens(token.access_token, token.expires_in, null);
        }
        return true;
      }));
  }

  /**
   * Creates an ApiRequest that supports automatic token refresh
   * @param relativeUrl Url to get
   */
  public getRequest<TResult>(relativeUrl: string): ApiRequest<TResult> {
    const apiRequest = new ApiRequest<TResult>(this._http, this)
      .get(this.getApiRoot() + relativeUrl)
      ;
    return apiRequest;
  }

  /**
   * Creates an ApiRequest that supports automatic token refresh
   * @param relativeUrl Url to get
   */
  public postRequest<TResult>(relativeUrl: string, body: any): ApiRequest<TResult> {
    const apiRequest = new ApiRequest<TResult>(this._http, this)
      .post(this.getApiRoot() + relativeUrl, body)
      ;
    return apiRequest;
  }

  /**
   * Creates an ApiRequest that supports automatic token refresh
   * @param relativeUrl Url to get
   */
  public putRequest<TResult>(relativeUrl: string, body: any): ApiRequest<TResult> {
    const apiRequest = new ApiRequest<TResult>(this._http, this)
      .put(this.getApiRoot() + relativeUrl, body)
      ;
    return apiRequest;
  }

  /**
   * Creates an ApiRequest that supports automatic token refresh
   * @param relativeUrl Url to get
   */
  public deleteRequest<TResult>(relativeUrl: string): ApiRequest<TResult> {
    const apiRequest = new ApiRequest<TResult>(this._http, this)
      .delete(this.getApiRoot() + relativeUrl);
    return apiRequest;
  }
}

export class ApiRequest<TResponse> {
  _body: any;
  _url: string;
  _method: Method;
  _allowAutomaticRefreshToken = true;
  _allowAutomaticRedirectToLogin = true;
  _allowAutomaticNotAuthorizedPage = true;
  _requiresTokenAuthentication = true;
  _unauthorizedFailureCount = 0;
  _attemptedPreemptiveRefreshToken = false;

  constructor(private _http: HttpClient, private _service: ApiService) {
  }

  public disableAutomaticNotAuthorizedPage(): ApiRequest<TResponse> {
    this._allowAutomaticNotAuthorizedPage = false;
    return this;
  }

  public disableAutomaticRefreshToken(): ApiRequest<TResponse> {
    this._allowAutomaticRefreshToken = false;
    return this;
  }

  public disableAutomaticRedirectToLogin(): ApiRequest<TResponse> {
    this._allowAutomaticRedirectToLogin = false;
    return this;
  }

  public doesNotRequireTokenAuthentication(): ApiRequest<TResponse> {
    this._requiresTokenAuthentication = false;
    return this;
  }

  public get(url: string): ApiRequest<TResponse> {
    this._url = url;
    this._method = Method.Get;

    return this;
  }

  public post(url: string, body: any): ApiRequest<TResponse> {
    this._body = body;
    this._url = url;
    this._method = Method.Post;

    return this;
  }

  public put(url: string, body: any): ApiRequest<TResponse> {
    this._body = body;
    this._url = url;
    this._method = Method.Put;

    return this;
  }

  public delete(url: string): ApiRequest<TResponse> {
    this._url = url;
    this._method = Method.Delete;

    return this;
  }

  private getHeaders(): any {
    const headers = {};

    const accessToken = this._service.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = 'Bearer ' + accessToken;
    }

    return headers;
  }

  private hasMaxNumberOfUnauthorizedFailures() {
    return this._unauthorizedFailureCount > MAX_UNAUTHORIZED_FAILURES;
  }

  /**
   * Builds the actual observable that can be used to get the api response
   */
  public build(): Observable<ApiResponse<TResponse>> {

    if (this._requiresTokenAuthentication && !this._service.getAccessToken()
      && !this._attemptedPreemptiveRefreshToken) {

      this._attemptedPreemptiveRefreshToken = true;
      const refreshObservable = this._service.getRefreshAccessToken();

      const originalRequest = this;

      return refreshObservable.pipe(flatMap((r) => {
        // Re-do build request after refresh attempt
        return originalRequest.build();
      }));

    }

    let observable: Observable<Object> = null;

    // Use withcredentials to send httponly cookies
    // const options = new RequestOptions({ withCredentials: true, headers: this.getHeaders() });

    const options = {
      withCredentials: true,
      headers: this.getHeaders()
    };

    switch (this._method) {
      case Method.Get:
        observable = this._http.get(this._url, options);
        break;
      case Method.Post:
        observable = this._http.post(this._url, this._body, options);
        break;
      case Method.Put:
        observable = this._http.put(this._url, this._body, options);
        break;
      case Method.Delete:
        observable = this._http.delete(this._url, options);
        break;
    }

    const newObservable = observable.pipe(
      map(res => {
        let objectResponse: ObjectResponse<TResponse> = null;
        try {
          objectResponse = res as ObjectResponse<TResponse>;
        } catch (ex) {
          objectResponse = { result: <any>res, requestId: 'unknown' };
        }
        this._service.setNetworkStatus(true);
        return { error: null, response: objectResponse ? objectResponse.result : null, requestId: objectResponse ? objectResponse.requestId : 'unknown' };
      }),
      catchError((err, original) => {
        if (err.status === 0) {
          this._service.setNetworkStatus(false);
        }

        let errorObj = { message: 'Unexpected error', errorMessage: err.toString(), statusCode: err.status, errorCode: 0, requestId: 'unknown' };

        let isInvalidTokenResponse = false;
        let isUnAuthApiAccessResponse = false;

        // Determine Error Type
        try {
          const errJson = err.json();
          if (!('errorCode' in errJson) && err.status === 401) {
            isInvalidTokenResponse = true;
          }
          else if (err.status === 401) {
            isUnAuthApiAccessResponse = true;
          }
          errorObj = errJson || errorObj;
        } catch (e) {
          if (err.status === 401) {
            isInvalidTokenResponse = true;
          }
        }

        if (isInvalidTokenResponse) {
          this._unauthorizedFailureCount++;
        }

        // If expired / invalid token response, attempt refresh token
        if (isInvalidTokenResponse && this._allowAutomaticRefreshToken && !this.hasMaxNumberOfUnauthorizedFailures()) {
          // Build up request to do refresh token
          const refreshAction = this._service.getRefreshAccessToken()

          const refreshChain = refreshAction.pipe(flatMap<boolean, ApiResponse<TResponse>>(refreshSuccessful => {
            if (!refreshSuccessful) {

              // Here we can either force redirect to login or return original error.
              if (this._allowAutomaticRedirectToLogin) {
                this._service.forceRedirectToLogin();
              }

              return of({ error: errorObj, response: null, requestId: errorObj.requestId });
            }

            // Chain original service call
            return original;
          }));

          return refreshChain;
        }

        // If unauth and out of options, can redirect to login
        if (isInvalidTokenResponse && this._allowAutomaticRedirectToLogin) {
          this._service.forceRedirectToLogin();
        }

        if (isUnAuthApiAccessResponse && this._allowAutomaticNotAuthorizedPage) {
          this._service.forceRedirectToUnAuthPage();
        }

        // Return whatever the error is
        return of({ error: errorObj, response: null, requestId: errorObj.requestId });
      }));

    return newObservable;
  }
}
